import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Select from "react-select";
import { toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import {
  FiAlertTriangle,
  FiEdit2,
  FiGlobe,
  FiPlus,
  FiTag,
  FiTrash2,
  FiUserPlus,
  FiUsers,
  FiX,
} from "react-icons/fi";

import {
  API_PROJECT_TIMESHEET_ACCESS,
  API_PROJECT_WORKSTREAM_TRACKING,
} from "../../config/Endpoints";
import NoRecordsAvailable from "../NoRecordsAvailable";
import "./DepartmentProjectAccess.css";

const endpointFor = (projectId) =>
  `${API_PROJECT_TIMESHEET_ACCESS}/${projectId}/timesheet-access`;

export default function DepartmentProjectAccess({ project, employees = [] }) {
  const [accessList, setAccessList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [employeeToRemove, setEmployeeToRemove] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("access");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryModal, setCategoryModal] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const isGlobalProject = project.timesheetAccessMode === "GLOBAL";
  const projectCategoryEndpoint = `${API_PROJECT_WORKSTREAM_TRACKING}/projects/${project.id}/categories`;

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken],
  );

  const availableEmployees = useMemo(() => {
    const assignedIds = new Set(accessList.map((item) => String(item.employeeId)));
    return employees.filter((item) => !assignedIds.has(String(item.value)));
  }, [accessList, employees]);

  const loadAccess = async () => {
    setLoading(true);
    try {
      const response = await axios.get(endpointFor(project.id), { headers });
      setAccessList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load project access.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isGlobalProject) loadAccess();
    // The project id is the access-list identity; token changes are handled on the next request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await axios.get(`${projectCategoryEndpoint}?includeInactive=true`, { headers });
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load project categories.");
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    // Categories are scoped by project id and refreshed after every mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const grantAccess = async () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        endpointFor(project.id),
        { employeeId: selectedEmployee.value },
        { headers: { ...headers, "Content-Type": "application/json" } },
      );
      toast.success("Timesheet access granted successfully.");
      setSelectedEmployee(null);
      setShowModal(false);
      await loadAccess();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to grant project access.");
      setLoading(false);
    }
  };

  const revokeAccess = async (employeeId) => {
    setLoading(true);
    try {
      await axios.delete(`${endpointFor(project.id)}/${employeeId}`, { headers });
      setAccessList((current) =>
        current.filter((item) => item.employeeId !== employeeId),
      );
      setEmployeeToRemove(null);
      toast.success("Timesheet access removed successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to remove project access.");
    } finally {
      setLoading(false);
    }
  };

  const initialsFor = (name) =>
    String(name || "Employee")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");

  const openCategoryModal = (category = null) => {
    setCategoryModal(category || "new");
    setCategoryName(category?.name || "");
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    const name = categoryName.trim().replace(/\s+/g, " ");
    if (!name) {
      toast.error("Category name is required.");
      return;
    }
    const editing = categoryModal !== "new" ? categoryModal : null;
    const duplicate = categories.some(
      (item) => item.id !== editing?.id && item.name?.trim().toLowerCase() === name.toLowerCase(),
    );
    if (duplicate) {
      toast.error("A category with this name already exists for the project.");
      return;
    }
    setCategorySaving(true);
    try {
      if (editing) {
        await axios.put(`${projectCategoryEndpoint}/${editing.id}`, { name }, { headers });
      } else {
        await axios.post(projectCategoryEndpoint, { name }, { headers });
      }
      toast.success(`Category ${editing ? "updated" : "created"} successfully.`);
      setCategoryModal(null);
      setCategoryName("");
      await loadCategories();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save project category.");
    } finally {
      setCategorySaving(false);
    }
  };

  const toggleCategoryStatus = async (category) => {
    const status = category.status === 1 ? 0 : 1;
    setCategorySaving(true);
    try {
      await axios.put(`${projectCategoryEndpoint}/${category.id}/status`, { status }, { headers });
      setCategories((current) => current.map((item) => item.id === category.id ? { ...item, status } : item));
      toast.success(`Category ${status === 1 ? "activated" : "deactivated"} successfully.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update category status.");
    } finally {
      setCategorySaving(false);
    }
  };

  const deleteCategory = async () => {
    if (!categoryToDelete) return;
    setCategorySaving(true);
    try {
      await axios.delete(`${projectCategoryEndpoint}/${categoryToDelete.id}`, { headers });
      setCategories((current) => current.filter((item) => item.id !== categoryToDelete.id));
      setCategoryToDelete(null);
      toast.success("Category deleted successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to delete project category.");
    } finally {
      setCategorySaving(false);
    }
  };

  return (
    <section className="department-access col-md-12 mt-4">
      <div className="department-access__tabs" role="tablist" aria-label="Project access and categories">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "access"}
          className={activeTab === "access" ? "department-access__tab department-access__tab--active" : "department-access__tab"}
          onClick={() => setActiveTab("access")}
        >
          {isGlobalProject ? <FiGlobe /> : <FiUsers />} Access
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "categories"}
          className={activeTab === "categories" ? "department-access__tab department-access__tab--active" : "department-access__tab"}
          onClick={() => setActiveTab("categories")}
        >
          <FiTag /> Categories
        </button>
      </div>

      {activeTab === "access" && (isGlobalProject ? (
        <div className="department-access__global">
          <span><FiGlobe /></span>
          <div>
            <h5>Organisation-wide access</h5>
            <p>All eligible employees can record time against this project. No individual employee assignment is required.</p>
          </div>
        </div>
      ) : <>
        <div className="department-access__header">
        <div className="department-access__heading">
          <span className="department-access__heading-icon"><FiUsers /></span>
          <div>
            <div className="department-access__title-line">
              <h5>Access</h5>
              <span className="department-access__count">
                {accessList.length} {accessList.length === 1 ? "employee" : "employees"}
              </span>
            </div>
           
          </div>
        </div>
        {![1, 3].includes(project.projectStatus) && (
          <button
            className="department-access__grant"
            type="button"
            onClick={() => setShowModal(true)}
            disabled={project.projectStatus === 2}
          >
            <FiUserPlus /> Grant Access
          </button>
        )}
      </div>

      {loading && accessList.length === 0 ? (
        <div className="department-access__loading">
          <ThreeDots height="24" width="48" radius="9" color="#0d6efd" />
        </div>
      ) : accessList.length === 0 ? (
        <div className="department-access__empty"><NoRecordsAvailable /></div>
      ) : (
        <div className="department-access__table-wrap">
          <table className="department-access__table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Designation</th>
                <th aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {accessList.map((item) => (
                <tr key={item.employeeId}>
                  <td>
                    <div className="department-access__employee">
                      <span className="department-access__avatar">{initialsFor(item.employeeName)}</span>
                      <strong>{item.employeeName || "-"}</strong>
                    </div>
                  </td>
                  <td><span className="department-access__tag">{item.department || "-"}</span></td>
                  <td>{item.designation || "-"}</td>
                  <td>
                    <button
                      type="button"
                      className="department-access__remove"
                      onClick={() => setEmployeeToRemove(item)}
                      disabled={loading}
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}

      {activeTab === "categories" && (
        <div className="department-categories">
          <div className="department-access__header">
            <div className="department-access__heading">
              <span className="department-access__heading-icon department-access__heading-icon--category"><FiTag /></span>
              <div>
                <div className="department-access__title-line">
                  <h5>Project Categories</h5>
                  <span className="department-access__count">
                    {categories.length} {categories.length === 1 ? "category" : "categories"}
                  </span>
                </div>
                <p>Only active categories are available when employees fill their timesheet.</p>
              </div>
            </div>
            {![1, 3].includes(project.projectStatus) && (
              <button
                type="button"
                className="department-access__grant"
                onClick={() => openCategoryModal()}
                disabled={project.projectStatus === 2 || categorySaving}
              >
                <FiPlus /> Add Category
              </button>
            )}
          </div>

          {categoriesLoading ? (
            <div className="department-access__loading">
              <ThreeDots height="24" width="48" radius="9" color="#0d6efd" />
            </div>
          ) : categories.length === 0 ? (
            <div className="department-access__empty"><NoRecordsAvailable /></div>
          ) : (
            <div className="department-access__table-wrap">
              <table className="department-access__table department-categories__table">
                <thead>
                  <tr>
                    <th>Category Name</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td><strong className="department-categories__name">{category.name}</strong></td>
                      <td>
                        <button
                          type="button"
                          className={category.status === 1 ? "department-categories__status department-categories__status--active" : "department-categories__status department-categories__status--inactive"}
                          onClick={() => toggleCategoryStatus(category)}
                          disabled={categorySaving || project.projectStatus === 2 || [1, 3].includes(project.projectStatus)}
                          title="Change category status"
                        >
                          <span /> {category.status === 1 ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td>
                        <div className="department-categories__actions">
                          <button type="button" className="department-categories__edit" onClick={() => openCategoryModal(category)} disabled={categorySaving || project.projectStatus === 2 || [1, 3].includes(project.projectStatus)}>
                            <FiEdit2 /> Edit
                          </button>
                          <button type="button" className="department-access__remove" onClick={() => setCategoryToDelete(category)} disabled={categorySaving || project.projectStatus === 2 || [1, 3].includes(project.projectStatus)}>
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="department-access-modal__backdrop" role="presentation">
          <div className="department-access-modal" role="dialog" aria-modal="true" aria-labelledby="grant-access-title">
              <div className="department-access-modal__header">
                <div>
                  <h5 id="grant-access-title">Grant Timesheet Access</h5>
                  <p>Select an employee who should be able to record time for this project.</p>
                </div>
                <button type="button" className="department-access-modal__close" onClick={() => setShowModal(false)} aria-label="Close"><FiX /></button>
              </div>
              <div className="department-access-modal__body">
                <label className="form-label">Employee</label>
                <Select
                  options={availableEmployees}
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  placeholder="Search employee"
                  isSearchable
                  isClearable
                />
                <small className="department-access-modal__hint">
                  The selected employee can belong to any department and can have access to multiple department projects.
                </small>
              </div>
              <div className="department-access-modal__footer">
                <button type="button" className="department-access-modal__cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="button" className="department-access__grant" onClick={grantAccess} disabled={loading}>
                  {loading ? "Saving..." : "Grant Access"}
                </button>
              </div>
          </div>
        </div>
      )}

      {employeeToRemove && (
        <div className="department-access-modal__backdrop" role="presentation">
          <div className="department-access-confirm" role="alertdialog" aria-modal="true" aria-labelledby="remove-access-title">
            <span className="department-access-confirm__icon"><FiAlertTriangle /></span>
            <h5 id="remove-access-title">Remove timesheet access?</h5>
            <p>
              <strong>{employeeToRemove.employeeName}</strong> will no longer be able to record time against <strong>{project.projectName}</strong>.
            </p>
            <div className="department-access-confirm__actions">
              <button type="button" className="department-access-modal__cancel" onClick={() => setEmployeeToRemove(null)} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="department-access-confirm__remove" onClick={() => revokeAccess(employeeToRemove.employeeId)} disabled={loading}>
                {loading ? "Removing..." : "Remove Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryModal && (
        <div className="department-access-modal__backdrop" role="presentation">
          <div className="department-access-modal" role="dialog" aria-modal="true" aria-labelledby="category-modal-title">
            <div className="department-access-modal__header">
              <div>
                <h5 id="category-modal-title">{categoryModal === "new" ? "Add Category" : "Edit Category"}</h5>
                <p>This category will be available for timesheets while its status is active.</p>
              </div>
              <button type="button" className="department-access-modal__close" onClick={() => setCategoryModal(null)} aria-label="Close"><FiX /></button>
            </div>
            <form onSubmit={saveCategory}>
              <div className="department-access-modal__body">
                <label className="form-label" htmlFor={`category-name-${project.id}`}>Category Name</label>
                <input
                  id={`category-name-${project.id}`}
                  className="department-categories__input"
                  value={categoryName}
                  onChange={(event) => setCategoryName(event.target.value.replace(/^\s+/, ""))}
                  placeholder="Enter category name"
                  maxLength={100}
                  autoFocus
                  required
                />
              </div>
              <div className="department-access-modal__footer">
                <button type="button" className="department-access-modal__cancel" onClick={() => setCategoryModal(null)} disabled={categorySaving}>Cancel</button>
                <button type="submit" className="department-access__grant" disabled={categorySaving || !categoryName.trim()}>
                  {categorySaving ? "Saving..." : categoryModal === "new" ? "Create Category" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {categoryToDelete && (
        <div className="department-access-modal__backdrop" role="presentation">
          <div className="department-access-confirm" role="alertdialog" aria-modal="true" aria-labelledby="delete-category-title">
            <span className="department-access-confirm__icon"><FiAlertTriangle /></span>
            <h5 id="delete-category-title">Delete category?</h5>
            <p>
              <strong>{categoryToDelete.name}</strong> will no longer be available for new timesheets. Historical entries will remain unchanged.
            </p>
            <div className="department-access-confirm__actions">
              <button type="button" className="department-access-modal__cancel" onClick={() => setCategoryToDelete(null)} disabled={categorySaving}>Cancel</button>
              <button type="button" className="department-access-confirm__remove" onClick={deleteCategory} disabled={categorySaving}>
                {categorySaving ? "Deleting..." : "Delete Category"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
